import React, { useEffect, useMemo, useState } from "react";

import api from "../services/api";
import DownloadLink from "./DownloadLink";
import MathText from "./MathText";

function Bloc({ titre, texte, vide }) {
  return (
    <div className="ap-grp-detail" style={{ marginBottom: 14 }}>
      <h2 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: 8 }}>{titre}</h2>
      {texte ? <div style={{ whiteSpace: "pre-wrap" }}><MathText text={texte} /></div> : <p className="text-muted mb-0">{vide}</p>}
    </div>
  );
}

export default function EnseignantDevoirs() {
  const [devoirs, setDevoirs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState("");
  const [manuel, setManuel] = useState("");
  const [selection, setSelection] = useState(null);

  useEffect(() => {
    api.get("devoirs/enseignant/")
      .then((r) => setDevoirs(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setErreur(e?.response?.data?.detail || "Impossible de charger les devoirs."))
      .finally(() => setLoading(false));
  }, []);

  const manuels = useMemo(() => [...new Set(devoirs.map((d) => d.manuel).filter(Boolean))], [devoirs]);
  const visibles = manuel ? devoirs.filter((d) => d.manuel === manuel) : devoirs;
  const contexte = (d) => [d.matiere, d.manuel, d.module].filter(Boolean).join(" · ");

  if (selection) {
    return (
      <div className="ap-page">
        <div className="ap-page-header">
          <div>
            <div className="ap-breadcrumb"><span>Contenu pédagogique</span><i className="bi bi-chevron-right"></i><span>Devoirs</span></div>
            <h1 className="ap-page-title">{selection.title}</h1>
            <p className="ap-page-desc">{contexte(selection)}</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {selection.pdf_url && (
              <DownloadLink url={selection.pdf_url} filename={`${selection.title}.pdf`} mimeType="application/pdf" className="btn btn-outline-secondary btn-sm">
                <i className="bi bi-file-pdf me-1"></i>Sujet PDF
              </DownloadLink>
            )}
            <button className="btn btn-outline-primary btn-sm" onClick={() => setSelection(null)}>
              <i className="bi bi-arrow-left me-1"></i>Tous les devoirs
            </button>
          </div>
        </div>
        <Bloc titre="Énoncé" texte={selection.description} vide="Aucun énoncé saisi." />
        <Bloc titre="Corrigé" texte={selection.correction} vide="Aucun corrigé saisi pour ce devoir." />
        <Bloc titre="Barème" texte={selection.bareme} vide="Aucun barème saisi pour ce devoir." />
      </div>
    );
  }

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Contenu pédagogique</span><i className="bi bi-chevron-right"></i><span>Devoirs</span></div>
          <h1 className="ap-page-title">Devoirs et corrigés</h1>
          <p className="ap-page-desc">Énoncés, corrigés et barèmes saisis dans l'administration.</p>
        </div>
        {manuels.length > 1 && (
          <select className="form-select form-select-sm" style={{ width: "auto" }} value={manuel} onChange={(e) => setManuel(e.target.value)} aria-label="Filtrer par manuel">
            <option value="">Tous les manuels</option>
            {manuels.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>
      {erreur && <div className="alert alert-danger">{erreur}</div>}
      {loading ? (
        <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
      ) : visibles.length === 0 ? (
        <div className="ap-grp-empty"><p className="text-muted">Aucun devoir disponible.</p></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibles.map((d) => (
            <button key={d.id} type="button" className="ap-grp-detail text-start" style={{ cursor: "pointer", border: "none", width: "100%" }} onClick={() => setSelection(d)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                <strong>{d.title}</strong>
                <span className={`ap-status-badge${d.published ? " active" : " inactive"}`}>{d.published ? "Publié" : "Brouillon"}</span>
                <span className="ap-role-badge" style={{ background: d.correction ? "#0f766e" : "#94a3b8" }}>{d.correction ? "Corrigé disponible" : "Sans corrigé"}</span>
              </div>
              <small className="text-muted">{contexte(d)}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
